import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FiShield as Shield, FiEye as Eye, FiEyeOff as EyeOff, FiPhone as Phone } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { AGENCY_PHONE_DISPLAY, AGENCY_PHONE_TEL } from '../constants/contact';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const successMessage = location.state?.message || '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) { setError('Email is required.'); return; }
    if (!password) { setError('Password is required.'); return; }

    setIsLoading(true);
    const result = await login(email, password);
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(
        result.error === 'Invalid login credentials'
          ? 'Incorrect email or password. Please try again.'
          : result.error
      );
    }
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <Link to="/" className="inline-flex items-center space-x-3 group mb-8">
            <div className="w-12 h-12 bg-gradient-to-br from-[#DC143C] to-[#B01030] rounded-lg flex items-center justify-center group-hover:shadow-lg transition-all duration-200">
              <span className="text-white font-bold text-xl">P</span>
            </div>
            <div className="text-left">
              <div className="text-lg font-bold text-gray-900">Pinoy General Insurance</div>
              <div className="text-xs text-gray-600">Services</div>
            </div>
          </Link>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Welcome Back</h2>
          <p className="text-gray-600">Sign in to access your insurance dashboard</p>
        </div>

        <div className="bg-white py-8 px-8 shadow-xl rounded-xl border border-gray-200">
          {successMessage && (
            <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
              {successMessage}
            </div>
          )}

          <form className="space-y-6" onSubmit={handleSubmit} noValidate>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#DC143C] focus:border-transparent transition-all duration-200"
                placeholder="Enter your email"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#DC143C] focus:border-transparent transition-all duration-200"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="h-4 w-4 text-[#DC143C] focus:ring-[#DC143C] border-gray-300 rounded"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                  Remember me
                </label>
              </div>
              <Link to="/forgot-password" className="text-sm text-[#0038A8] hover:underline">
                Forgot your password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-[#DC143C] text-white py-3 px-4 rounded-lg font-semibold hover:bg-[#B01030] focus:ring-4 focus:ring-[#DC143C]/20 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <div className="spinner"></div>
                  Signing In...
                </>
              ) : (
                <>
                  <Shield size={20} /> Sign In
                </>
              )}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-gray-200 text-center space-y-2">
            <p className="text-sm text-gray-600">
              Don't have an account?{' '}
              <Link to="/signup" className="text-[#0038A8] hover:underline font-medium">
                Sign up
              </Link>
            </p>
            {/* /quote is the proven lead-capture surface and stays one tap from
                the front door, for visitors who are not clients yet. */}
            <p className="text-sm text-gray-600">
              Need a quote?{' '}
              <Link to="/quote" className="text-[#0038A8] hover:underline font-medium">
                Get a free quote
              </Link>
            </p>
          </div>
        </div>

        {/* Persistent, not tucked into a footer: someone who cannot get in is
            the person most likely to need a human, and this screen is now the
            first thing they see. */}
        <a
          href={AGENCY_PHONE_TEL}
          className="flex items-center justify-center gap-2 w-full bg-white border border-gray-200 rounded-xl py-3 px-4 text-gray-800 font-semibold hover:bg-gray-50 transition-colors shadow-sm"
        >
          <Phone size={18} className="text-[#DC143C]" />
          Call or text us: {AGENCY_PHONE_DISPLAY}
        </a>
      </div>
    </div>
  );
};

export default LoginPage;
